import { useMemo } from 'react'
import type { GrowthCategory, GrowthContent } from '@mc/api'
import { useGrowthCategories, useGrowthContent } from 'libs/client/api/growth'
import { queryClient } from 'libs/client/query-client'
import { getInstructor1x1ImgSrc } from './components/InstructorImage'

export const useCategories = () => {
  const { data: categories, isLoading } = useGrowthCategories()

  const sortedCategories = useMemo(() => {
    if (isLoading || !categories) return []
    const theCategories = categories.filter(
      (cat) => (cat?.parentId || null) === null,
    )
    theCategories.sort((a, b) => a.name.localeCompare(b.name))
    return theCategories
  }, [categories, isLoading])

  return { categories: sortedCategories, isLoading }
}

const preloaded = new Set<string>()

export const preloadCategoryImages = ({
  categoryId,
}: {
  categoryId: GrowthCategory['id']
}) => {
  const k = String(categoryId)
  if (preloaded.has(k)) return

  const cached = queryClient.getQueryData<GrowthContent[]>(['growth-content'])
  const classesToPreload = (cached || [])?.filter((c) =>
    c.categories.some((cat) => cat.id === categoryId),
  )
  classesToPreload.forEach((course) => {
    if (
      course &&
      typeof course === 'object' &&
      'primary1x1' in course &&
      typeof course.primary1x1 === 'string'
    ) {
      new Image().src = getInstructor1x1ImgSrc(course.primary1x1)
    }
  })

  preloaded.add(k)
}

export const useCoursesByCategory = (slug: string) => {
  const { data: growthContent, isLoading, isError } = useGrowthContent()

  const categoryId = useCategoryId({ categorySlug: slug })
  const growthContentForCategory = useMemo(
    () =>
      (growthContent || []).filter((c) =>
        c.categories.some((cat) => cat.id === categoryId),
      ),
    [categoryId, growthContent],
  )

  return {
    coursesByCategory: growthContentForCategory,
    isValidating: isLoading,
    error: isError,
  }
}

export const useCoursesBySubcategory = (
  categorySlug: string,
  subcat: Subcat,
) => {
  const { coursesByCategory } = useCoursesByCategory(categorySlug)
  const subcatCourses =
    coursesByCategory?.filter((c) => subcat.courses.includes(c.slug)) || []

  return {
    subcatCourses,
  }
}

export type Subcat = {
  id: number
  name: string
  slug: string
  courses: string[] // course slugs
}

export const useSubcategoriesForCategory = ({
  categorySlug,
}: {
  categorySlug: string
}) => {
  const { data: categories, isLoading } = useGrowthCategories()
  const { data: classes } = useGrowthContent()

  if (isLoading || !categories || !classes) return null

  const category = categories.find((cat) => cat.slug === categorySlug)

  const subcategories = categories.filter(
    (catOrSubcat) => catOrSubcat.parentId === category?.id,
  )

  if (!category || !subcategories.length) return null

  return {
    id: category.id,
    name: category.name,
    slug: category.slug,
    subcategories: subcategories.map((sc) => ({
      id: sc.id,
      name: sc.name,
      slug: sc.slug,
      courses: classes
        .filter((c) => !!c.subcategories.find((s) => s.id === sc.id))
        .map((c) => c.slug),
    })),
  }
}

const useCategoryId = ({ categorySlug }: { categorySlug: string }) => {
  const { data: categories } = useGrowthCategories()
  const category = categories?.find((cat) => cat.slug === categorySlug)
  return category?.id
}
